import React from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { captureRef } from './lib/attribution';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import MfaSetupPage from './pages/MfaSetupPage';
import NotificationsInbox from './pages/NotificationsInbox';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PatientDashboard from './pages/PatientDashboard';
import PatientToday from './pages/PatientToday';
import PatientAssessment from './pages/PatientAssessment';
import ClinicianDashboard from './pages/ClinicianDashboard';
import ClinicianCredentialing from './pages/ClinicianCredentialing';
import ClinicianReadmissionRisk from './pages/ClinicianReadmissionRisk';
import ClinicianAssessment from './pages/ClinicianAssessment';
import AgencyDashboard from './pages/AgencyDashboard';
import AgencyFindClinicians from './pages/AgencyFindClinicians';
import AdminDashboard from './pages/AdminDashboard';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminSafetyShield from './pages/AdminSafetyShield';
import AdminPaymentsOps from './pages/AdminPaymentsOps';
import AdminObservability from './pages/AdminObservability';
import AdminNotificationSuppressions from './pages/AdminNotificationSuppressions';
import TelehealthRoom from './pages/TelehealthRoom';
import VisitPass from './pages/VisitPass';
import AboutPage from './pages/AboutPage';
import HowItWorksPage from './pages/HowItWorksPage';
import ServicesPage from './pages/ServicesPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import ServiceLandingPage from './pages/ServiceLandingPage';
import ResourcesPage from './pages/ResourcesPage';
import ArticlePage from './pages/ArticlePage';
import LocalCareIndexPage from './pages/LocalCareIndexPage';
import SeoLandingPage from './pages/SeoLandingPage';
import LocalCarePage from './pages/LocalCarePage';
import DischargePlannersPage from './pages/DischargePlannersPage';
import ForFamiliesPage from './pages/ForFamiliesPage';
import ForFamiliesAdvanceDirectiveStatePage from './pages/ForFamiliesAdvanceDirectiveStatePage';
import DischargeApiDocs from './pages/DischargeApiDocs';
import ForCliniciansPage from './pages/ForCliniciansPage';
import DisciplineLandingPage from './pages/DisciplineLandingPage';
import AISafetyPage from './pages/AISafetyPage';
import CareLibraryPage from './pages/CareLibraryPage';
import { DISCIPLINES } from './data/disciplines';
import RcmDashboard from './pages/RcmDashboard';
import OperationsExecutive from './pages/OperationsExecutive';
import ReferralsFhirIngest from './pages/ReferralsFhirIngest';
import DenialPrediction from './pages/DenialPrediction';
import SuitePage from './pages/SuitePage';
import OrgTeamPage from './pages/OrgTeamPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import ClinicianPayouts from './pages/ClinicianPayouts';
import ClinicianBanking from './pages/ClinicianBanking';
import ClinicianRiskMonitor from './pages/ClinicianRiskMonitor';
import PatientBilling from './pages/PatientBilling';
import PlannerDashboard from './pages/PlannerDashboard';
import PayerDashboard from './pages/PayerDashboard'; // parked — kept for resurrection (see ParkedWorkspace)
import ParkedWorkspace from './pages/ParkedWorkspace';
import DailyCheckIn from './pages/DailyCheckIn';
import SmartScheduling from './pages/SmartScheduling';
import FamilyCaregiverPortal from './pages/FamilyCaregiverPortal';
import JoinCareCircle from './pages/JoinCareCircle';
import FeeSchedules from './pages/FeeSchedules'; // eslint-disable-line no-unused-vars -- de-routed (Fix-Now Part 2 §1), preserved for monetization re-enable
import PilotPricingPage from './pages/PilotPricingPage';
import VoiceNotes from './pages/VoiceNotes';
import DocumentSharing from './pages/DocumentSharing';
import RiskAssessment from './pages/RiskAssessment';
import FindClinicians from './pages/FindClinicians';
import ClinicalNotes from './pages/ClinicalNotes';
import MessagesPage from './pages/MessagesPage';
import OffersPage from './pages/OffersPage';
import CheckoutSuccess from './pages/CheckoutSuccess';
import MarketplacePage from './pages/MarketplacePage';
import ClinicianProfilePage from './pages/ClinicianProfilePage';
import TrustSafetyPage from './pages/TrustSafetyPage';
import TrustPortalPage from './pages/TrustPortalPage';
import StatusPage from './pages/StatusPage';
import CaseStudiesPage from './pages/CaseStudiesPage';
import SurveyReadiness from './pages/SurveyReadiness';
import EligibilityVerify from './pages/EligibilityVerify';
import RouteOptimizer from './pages/RouteOptimizer';
import EraIngest from './pages/EraIngest';
import AdminPartners from './pages/AdminPartners';
import PartnerIntake from './pages/PartnerIntake';
import VisitBrief from './pages/VisitBrief';
import PatientCarePlan from './pages/PatientCarePlan';
import PublicSurvey from './pages/PublicSurvey';
import CareCircle from './pages/CareCircle';
import FamilyDigest from './pages/FamilyDigest';
import ProviderPnL from './pages/ProviderPnL';
import WorkforceShifts from './pages/WorkforceShifts';
import PricingCalculator from './pages/PricingCalculator';
import FaqPage from './pages/FaqPage';
import TrustEvidence from './pages/TrustEvidence';
import AdminPayerVbc from './pages/AdminPayerVbc';
import AdminAutoStatements from './pages/AdminAutoStatements';
import AdminDataNetwork from './pages/AdminDataNetwork';
import ClinicianCeu from './pages/ClinicianCeu';
import AdminCredentialingLapse from './pages/AdminCredentialingLapse';
import AdminExclusionScreening from './pages/AdminExclusionScreening';
import AskCara from './pages/AskCara';
import AdminReferralSources from './pages/AdminReferralSources';
import ClinicianEvvPage from './pages/ClinicianEvv';
import AdminEvvAudit from './pages/AdminEvvAudit';
import AdminIncidents from './pages/AdminIncidents';
import AdminClinicianRetention from './pages/AdminClinicianRetention';
import AdminTelehealthNoShows from './pages/AdminTelehealthNoShows';
import AdminReferralSla from './pages/AdminReferralSla';
import AdminPriorAuth from './pages/AdminPriorAuth';
import AdminReadmissionRisk from './pages/AdminReadmissionRisk';
import AdminVbcQualityMeasures from './pages/AdminVbcQualityMeasures';
import AdminCapacityForecast from './pages/AdminCapacityForecast';
import AdminBurnoutPulse from './pages/AdminBurnoutPulse';
import AdminActivityLedger from './pages/AdminActivityLedger';
import AdminCompliance from './pages/AdminCompliance';
import AdminAiValidation from './pages/AdminAiValidation';
import AdminIntakeInbox from './pages/AdminIntakeInbox';
import AdminReferralCrm from './pages/AdminReferralCrm';
import AdminHieConsole from './pages/AdminHieConsole';
import AdminNetworkMap from './pages/AdminNetworkMap';
import AdminRoiMetrics from './pages/AdminRoiMetrics';
// Iter 179 · Controlled demo environment
import DemoAccessGuard from './pages/demo/DemoAccessGuard';
import DemoLayout from './pages/demo/DemoLayout';
import DemoDeck from './pages/demo/DemoDeck';
import {
  DemoOverview, DemoTransitions, DemoPatientDetail,
  DemoNetwork, DemoInsights, DemoPilotDiscussion,
} from './pages/demo/DemoScreens';
// Iter 172 · Category-owning pillar landing pages for SEO reposition.
import DischargePlanningSoftwarePage from './pages/DischargePlanningSoftwarePage';
import PostAcuteCarePlatformPage from './pages/PostAcuteCarePlatformPage';
import CareTransitionsPage from './pages/CareTransitionsPage';
// Iter 173 · Audience-specific solutions landing pages.
import SolutionsSegmentPage from './pages/SolutionsSegmentPage';
// Iter 174 · Knowledge Center (B2B guides for discharge planners).
import GuidesPage from './pages/GuidesPage';
import GuidePage from './pages/GuidePage';
// Iter 175 · Public post-acute provider directory.
import {
  ProvidersLandingPage,
  ProviderLevelIndexPage,
  ProviderStatePage,
  ProviderDetailPage,
} from './pages/ProviderDirectoryPages';
// Iter 176 · Competitive comparison pages (/compare/*).
import ComparePage from './pages/ComparePage';
import WatchDemoPage from './pages/WatchDemoPage';
import MySharesPage from './pages/MySharesPage';
import AdminOrchestrator from './pages/AdminOrchestrator';
import PlannerOrchestrator from './pages/PlannerOrchestrator';
import AdvanceCarePlanning from './pages/AdvanceCarePlanning';
import AdminNpsAnalytics from './pages/AdminNpsAnalytics';
import AdminSurgePricing from './pages/AdminSurgePricing';
import AdminCodingQaSweep from './pages/AdminCodingQaSweep';
import PatientTeleTriage from './pages/PatientTeleTriage';
import PartnerDirectoryPage, { PartnerDetailPage } from './pages/PartnerDirectoryPage';
import AdminPartnerDirectory from './pages/AdminPartnerDirectory';
import MultiLocationDashboard from './pages/MultiLocationDashboard';
import PatientEducation from './pages/PatientEducation';
import AuditExport from './pages/AuditExport';
import AICareNavigatorPage from './pages/AICareNavigatorPage';
import AIPracticeCoachPage from './pages/AIPracticeCoachPage';
import PracticeMigration from './pages/PracticeMigration';
import HelpCenter from './pages/HelpCenter';
import SupportCenter from './pages/SupportCenter';
import AdminSupportInbox from './pages/AdminSupportInbox';
import AdminOpsDashboard from './pages/AdminOpsDashboard';
import AdminTenantHealthDashboard from './pages/AdminTenantHealthDashboard';
import AdminClaimPage from './pages/AdminClaimPage';
import SavedSearches from './pages/SavedSearches';
import SavedClinicians from './pages/SavedClinicians';
import BenefitsNavigator from './pages/BenefitsNavigator';
import ClinicianPractice from './pages/ClinicianPractice';
import MicroAgencyPage from './pages/MicroAgencyPage';
import CareRequests from './pages/CareRequests';
import ClinicianCareRequests from './pages/ClinicianCareRequests';
import ClinicianInbox from './pages/ClinicianInbox';
import {
  ClinicianSchedulePage,
  ClinicianInsurancePage,
  ClinicianPracticeProfilePage,
  ClinicianPublicProfilePage,
} from './pages/ClinicianSettings';
import PatientFindCare from './pages/PatientFindCare';
import PlatformOnePager from './pages/PlatformOnePager';
import PrivacyDashboard from './pages/PrivacyDashboard';
import SpineDischargePlanner from './pages/SpineDischargePlanner';
import SpineClinicianOffers from './pages/SpineClinicianOffers';
import SpineAdminEscalations from './pages/SpineAdminEscalations';
import SpineAdminOrganizations from './pages/SpineAdminOrganizations';
import SpineClinicianWatches from './pages/SpineClinicianWatches';
import SpineAdminWatches from './pages/SpineAdminWatches';
import SpineHospitalScoreboard from './pages/SpineHospitalScoreboard';
import ProviderOSDashboard from './pages/ProviderOSDashboard';
import ProviderOSWork from './pages/ProviderOSWork';
import ProviderOSWorkDetail from './pages/ProviderOSWorkDetail';
import ProviderHome from './pages/ProviderHome';
import ProviderOSDocuments from './pages/ProviderOSDocuments';
import ProviderOSPayouts from './pages/ProviderOSPayouts';
import ProviderOSOnboard from './pages/ProviderOSOnboard';
import FamilyStatusView from './pages/FamilyStatusView';
import FamilyShareManage from './pages/FamilyShareManage';
import FamilyMarketplace from './pages/FamilyMarketplace';
// PART C (Feb 2026) — role-aware dashboard focus shell. Additive: wraps
// existing dashboards with a calm focus view; legacy detail one click away.
import { DashboardFocusShell } from './components/dashboard-focus/DashboardFocusShell';
import { FeatureGate } from './components/brand/FeatureGate';

import { AccessibilityModeProvider } from './contexts/AccessibilityModeContext';
import { LynkCopilot } from './components/brand/LynkCopilot';
import { CaraDrawer } from './components/brand/CaraDrawer';
import { CommandPalette } from './components/brand/CommandPalette';
import { EmergencyButton } from './components/brand/EmergencyButton';
import { ProactiveLynk } from './components/brand/ProactiveLynk';
import { OnboardingConcierge } from './components/brand/OnboardingConcierge';
import { SessionTimeout } from './components/brand/SessionTimeout';
import { ConsentGate } from './components/brand/ConsentGate';
import { PublicCtaBar } from './components/brand/PublicCtaBar';
import { PwaInstallPrompt } from './components/brand/PwaInstallPrompt';

import './index.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bg flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-cn-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Send authenticated users to their own role's dashboard, never leak another role's view.
    const DASH = {
      patient: '/patient/dashboard',
      clinician: '/provider-os',
      agency: '/provider-os',
      discharge_planner: '/planner/dashboard',
      hospital_admin: '/planner/dashboard',
      payer: '/payer/dashboard',
      admin: '/admin/dashboard',
    };
    return <Navigate to={DASH[user.role] || '/'} replace />;
  }
  
  return children;
};

// Auth Guard
const AuthGuard = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bg flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-cn-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    switch(user.role) {
      case 'patient':
        return <Navigate to="/patient/dashboard" replace />;
      case 'clinician':
        return <Navigate to="/provider-os" replace />;
      case 'agency':
        return <Navigate to="/provider-os" replace />;
      case 'discharge_planner':
        return <Navigate to="/planner/dashboard" replace />;
      case 'hospital_admin':
        return <Navigate to="/planner/dashboard" replace />;
      case 'payer':
        return <Navigate to="/payer/dashboard" replace />;
      case 'admin':
        return <Navigate to="/admin/dashboard" replace />;
      default:
        return children;
    }
  }
  
  return children;
};

function AppRoutes() {
  React.useEffect(() => { captureRef(); }, []);
  const { mfaSetupToken } = useAuth();
  // Emergent Google OAuth callback (Issue 14b). Detect `#session_id=…`
  // synchronously during render so we exchange the session BEFORE any
  // ProtectedRoute or the AuthProvider's `/auth/me` race can fire.
  if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  // Iter 155 · MFA Enforcement — if the user is holding a scoped
  // ``mfa_setup_only`` token (backend told them they must enroll before
  // proceeding), lock ALL navigation to /mfa-setup. The setup page itself
  // provides the only escape hatch (Sign out).
  if (typeof window !== 'undefined'
      && mfaSetupToken
      && window.location.pathname !== '/mfa-setup') {
    return <Navigate to="/mfa-setup" replace />;
  }
  return (
    <Routes>
      {/* Iter 155 · MFA setup gate — reachable via the scoped setup token
          returned by login/register/google when the user's role requires
          MFA and they haven't enrolled yet. Not protected by ProtectedRoute
          because the caller has no full session yet. */}
      <Route path="/mfa-setup" element={<MfaSetupPage />} />

      {/* Iter 161.1 · Role-agnostic notifications inbox. Linked from
          the FocusHeaderChip bell across every dashboard. */}
      <Route path="/notifications" element={<ProtectedRoute><NotificationsInbox /></ProtectedRoute>} />

      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      {/* Iter 179 · IP protection — /watch demo now requires auth so the
          workflow reveal it contains is only seen by controlled viewers. */}
      <Route path="/watch" element={<ProtectedRoute><WatchDemoPage /></ProtectedRoute>} />

      {/* Iter 179 · Controlled demo environment. Two-tier access: any
          authenticated user OR a granted prospect-preview session token. */}
      <Route
        path="/demo/preview"
        element={<DemoAccessGuard><DemoLayout /></DemoAccessGuard>}
      >
        <Route index element={<DemoOverview />} />
        <Route path="transitions" element={<DemoTransitions />} />
        <Route path="patient/:id" element={<DemoPatientDetail />} />
        <Route path="network" element={<DemoNetwork />} />
        <Route path="insights" element={<DemoInsights />} />
        <Route path="pilot" element={<DemoPilotDiscussion />} />
        <Route path="deck" element={<DemoDeck />} />
      </Route>
      <Route path="/suites/:slug" element={<SuitePage />} />
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
      <Route 
        path="/org/team" 
        element={
          <ProtectedRoute allowedRoles={['agency', 'discharge_planner', 'admin']}>
            <OrgTeamPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      {/* Iter 172 · Category-owning pillar landing pages (SEO reposition). */}
      <Route path="/discharge-planning-software" element={<DischargePlanningSoftwarePage />} />
      <Route path="/post-acute-care-platform" element={<PostAcuteCarePlatformPage />} />
      <Route path="/care-transitions" element={<CareTransitionsPage />} />
      {/* Iter 173 · Audience-specific solutions surface (/solutions/{segment}). */}
      <Route path="/solutions/:segment" element={<SolutionsSegmentPage />} />
      {/* Iter 174 · CaraLoom Knowledge Center — B2B guides. */}
      <Route path="/guides" element={<GuidesPage />} />
      <Route path="/guides/:slug" element={<GuidePage />} />
      {/* Iter 175 · Post-acute provider directory (4 routes). */}
      <Route path="/providers" element={<ProvidersLandingPage />} />
      <Route path="/providers/:levelOfCare" element={<ProviderLevelIndexPage />} />
      <Route path="/providers/:levelOfCare/:state" element={<ProviderStatePage />} />
      <Route path="/providers/:levelOfCare/:state/:slug" element={<ProviderDetailPage />} />
      {/* Iter 176 · Vendor comparison pages (high-intent commercial keywords). */}
      <Route path="/compare/:slug" element={<ComparePage />} />
      <Route path="/services" element={
        <FeatureGate featureId="seo.service-landing-pages"><ServicesPage /></FeatureGate>
      } />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pricing" element={<PilotPricingPage />} />
      {/* Fix-Now Part 2 §1 — calculator de-routed behind the archive gate;
          PricingCalculator.jsx preserved untouched. */}
      <Route path="/pricing/calculator" element={
        <FeatureGate featureId="monetization.pricing-calculator"><PricingCalculator /></FeatureGate>
      } />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/trust/evidence" element={
        <ProtectedRoute allowedRoles={['admin']}><TrustEvidence /></ProtectedRoute>
      } />
      <Route path="/admin/payer-vbc" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminPayerVbc /></ProtectedRoute>
      } />
      <Route path="/admin/auto-statements" element={
        <ProtectedRoute allowedRoles={['admin', 'agency', 'provider']}><AdminAutoStatements /></ProtectedRoute>
      } />
      <Route path="/admin/data-network" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminDataNetwork /></ProtectedRoute>
      } />
      <Route path="/clinician/ceu" element={
        <ProtectedRoute allowedRoles={['clinician', 'admin']}><ClinicianCeu /></ProtectedRoute>
      } />
      <Route path="/admin/credentialing" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminCredentialingLapse /></ProtectedRoute>
      } />
      <Route path="/admin/exclusions" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminExclusionScreening /></ProtectedRoute>
      } />
      <Route path="/ask-cara" element={<AskCara />} />
      <Route path="/operations/referral-sources" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminReferralSources /></ProtectedRoute>
      } />
      {/* Iter 168 · Sidebar-friendly alias so the /admin/referral-sources
          link in the ADMIN_NAV resolves without redirect. The page
          itself lives under /operations/... for historical reasons. */}
      <Route path="/admin/referral-sources" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminReferralSources /></ProtectedRoute>
      } />
      <Route path="/clinician/evv" element={
        <ProtectedRoute allowedRoles={['clinician', 'admin']}><ClinicianEvvPage /></ProtectedRoute>
      } />
      <Route path="/admin/evv" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminEvvAudit /></ProtectedRoute>
      } />
      <Route path="/admin/incidents" element={
        <ProtectedRoute allowedRoles={['admin', 'manager', 'operations_lead']}><AdminIncidents /></ProtectedRoute>
      } />
      <Route path="/admin/clinician-retention" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminClinicianRetention /></ProtectedRoute>
      } />
      <Route path="/admin/telehealth-no-shows" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminTelehealthNoShows /></ProtectedRoute>
      } />
      <Route path="/admin/referral-sla" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminReferralSla /></ProtectedRoute>
      } />
      <Route path="/admin/prior-auth" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminPriorAuth /></ProtectedRoute>
      } />
      <Route path="/admin/readmission-risk" element={
        <ProtectedRoute allowedRoles={['admin', 'provider']}><AdminReadmissionRisk /></ProtectedRoute>
      } />
      <Route path="/admin/vbc/measures" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminVbcQualityMeasures /></ProtectedRoute>
      } />
      <Route path="/admin/capacity-forecast" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminCapacityForecast /></ProtectedRoute>
      } />
      <Route path="/admin/burnout-pulse" element={
        <ProtectedRoute allowedRoles={['admin', 'provider']}><AdminBurnoutPulse /></ProtectedRoute>
      } />
      <Route path="/admin/activity-ledger" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminActivityLedger /></ProtectedRoute>
      } />
      <Route path="/admin/compliance" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminCompliance /></ProtectedRoute>
      } />
      <Route path="/admin/ai-validation" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminAiValidation /></ProtectedRoute>
      } />
      {/* Iter 167 · Pre-Admission Intake Inbox (ArcHouse-parity). Admins
          + provider org admins triage inbound referral packets. */}
      <Route path="/admin/intake" element={
        <ProtectedRoute allowedRoles={['admin', 'provider', 'clinician_admin', 'org_admin', 'intake_coordinator']}><AdminIntakeInbox /></ProtectedRoute>
      } />
      {/* Iter 169 · Referral CRM — facility profiles + notes + tasks. */}
      <Route path="/admin/crm" element={
        <ProtectedRoute allowedRoles={['admin', 'provider', 'clinician_admin', 'org_admin', 'intake_coordinator']}><AdminReferralCrm /></ProtectedRoute>
      } />
      {/* Iter 171 · HIE Console — audit view of every Metriport pull. */}
      <Route path="/admin/hie" element={
        <ProtectedRoute allowedRoles={['admin', 'provider', 'clinician_admin', 'org_admin', 'intake_coordinator']}><AdminHieConsole /></ProtectedRoute>
      } />
      {/* Iter 179 · National Network Map — coverage tiles + recruiting gaps. */}
      <Route path="/admin/network-map" element={
        <ProtectedRoute allowedRoles={['admin', 'provider', 'clinician_admin', 'org_admin', 'intake_coordinator']}><AdminNetworkMap /></ProtectedRoute>
      } />
      {/* Iter 179 · ROI metrics editor for the homepage + hospitals landing. */}
      <Route path="/admin/roi-metrics" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminRoiMetrics /></ProtectedRoute>
      } />
      {/* Iter 141 · Family-Facing Share UI — self-service page for anyone
          who has minted a signed download link (clinician, patient, admin). */}
      <Route path="/me/shares" element={
        <ProtectedRoute><MySharesPage /></ProtectedRoute>
      } />
      <Route path="/admin/orchestrator" element={
        <ProtectedRoute allowedRoles={['admin', 'discharge_planner', 'hospital_admin']}><AdminOrchestrator /></ProtectedRoute>
      } />
      <Route path="/admin/ops/notifications" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminNotificationSuppressions /></ProtectedRoute>
      } />
      <Route path="/planner/orchestrator" element={
        <ProtectedRoute allowedRoles={['discharge_planner', 'hospital_admin', 'admin']}><PlannerOrchestrator /></ProtectedRoute>
      } />
      <Route path="/planner/acp" element={
        <ProtectedRoute allowedRoles={['discharge_planner', 'hospital_admin', 'admin', 'clinician']}><AdvanceCarePlanning /></ProtectedRoute>
      } />
      <Route path="/admin/nps" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminNpsAnalytics /></ProtectedRoute>
      } />
      <Route path="/admin/surge-pricing" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminSurgePricing /></ProtectedRoute>
      } />
      <Route path="/admin/coding-qa-sweep" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminCodingQaSweep /></ProtectedRoute>
      } />
      <Route path="/triage" element={
        <ProtectedRoute><PatientTeleTriage /></ProtectedRoute>
      } />
      <Route path="/partners" element={<PartnerDirectoryPage />} />
      <Route path="/partners/:slug" element={<PartnerDetailPage />} />
      {/* Platform Features one-pager (print-to-PDF). Public, additive. */}
      <Route path="/platform-onepager" element={<PlatformOnePager />} />
      <Route path="/features" element={<PlatformOnePager />} />
      <Route path="/admin/network/directory" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminPartnerDirectory /></ProtectedRoute>
      } />
      <Route path="/provider/multi-location" element={
        <ProtectedRoute allowedRoles={['agency', 'admin']}><MultiLocationDashboard /></ProtectedRoute>
      } />
      <Route path="/patient/education" element={<PatientEducation />} />
      <Route path="/patient/education/:slug" element={<PatientEducation />} />
      <Route path="/compliance/audit-export" element={
        <ProtectedRoute allowedRoles={['admin', 'agency', 'provider', 'clinician']}><AuditExport /></ProtectedRoute>
      } />
      <Route path="/services/:slug" element={
        <FeatureGate featureId="seo.service-landing-pages"><ServiceLandingPage /></FeatureGate>
      } />
      {/* Auto-generated long-tail SEO landings (Feb 2026 content-velocity).
          Renders dynamically from /api/seo/landing/{slug}; only approved
          pages 200, drafts 404 → defensively redirect to /home-care. */}
      {/* V1-archived routes — gated by FeatureGate (see caraloom_v2_ia.js
          + /app/memory/CARALOOM_V2_ARCHIVE_PLAN.md). The route still exists
          for prerender + recoverability; the page renders <ArchivedFeaturePage />
          until isFeatureLive() returns true again. */}
      <Route path="/care/:slug" element={
        <FeatureGate featureId="seo.content-velocity-engine"><SeoLandingPage /></FeatureGate>
      } />
      <Route path="/marketplace" element={
        <FeatureGate featureId="care.public-marketplace"><MarketplacePage /></FeatureGate>
      } />
      <Route path="/clinicians/:id" element={<ClinicianProfilePage />} />
      <Route path="/trust-safety" element={<TrustSafetyPage />} />
      <Route path="/trust" element={<TrustPortalPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/customers" element={<CaseStudiesPage />} />
      <Route path="/compliance/survey-readiness" element={
        <ProtectedRoute><SurveyReadiness /></ProtectedRoute>
      } />
      <Route path="/clinician/eligibility" element={
        <ProtectedRoute><EligibilityVerify /></ProtectedRoute>
      } />
      <Route path="/clinician/route" element={
        <ProtectedRoute><RouteOptimizer /></ProtectedRoute>
      } />
      <Route path="/clinician/era" element={
        <ProtectedRoute><EraIngest /></ProtectedRoute>
      } />
      <Route path="/admin/partners" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminPartners /></ProtectedRoute>
      } />
      <Route path="/partners/:slug/refer" element={<PartnerIntake />} />
      <Route path="/clinician/visit-brief/:appointmentId" element={
        <ProtectedRoute><VisitBrief /></ProtectedRoute>
      } />
      <Route path="/patient/care-plan" element={
        <ProtectedRoute allowedRoles={['patient']}><PatientCarePlan /></ProtectedRoute>
      } />
      <Route path="/patient/care-circle" element={
        <ProtectedRoute allowedRoles={['patient']}><CareCircle /></ProtectedRoute>
      } />
      <Route path="/patient/this-week" element={
        <ProtectedRoute allowedRoles={['patient']}><FamilyDigest /></ProtectedRoute>
      } />
      <Route path="/operations/pnl" element={
        <ProtectedRoute allowedRoles={['agency', 'clinician', 'admin']}><ProviderPnL /></ProtectedRoute>
      } />
      <Route path="/workforce/shifts" element={
        <ProtectedRoute allowedRoles={['clinician', 'admin', 'agency']}><WorkforceShifts /></ProtectedRoute>
      } />
      <Route path="/survey/:token" element={<PublicSurvey />} />
      <Route path="/ai-safety" element={<AISafetyPage />} />
      <Route path="/care-library" element={
        <FeatureGate featureId="seo.care-library"><CareLibraryPage /></FeatureGate>
      } />
      <Route path="/resources" element={
        <FeatureGate featureId="seo.authority-articles"><ResourcesPage /></FeatureGate>
      } />
      <Route path="/resources/:slug" element={
        <FeatureGate featureId="seo.authority-articles"><ArticlePage /></FeatureGate>
      } />
      <Route path="/home-care" element={
        <FeatureGate featureId="seo.local-care-pages"><LocalCareIndexPage /></FeatureGate>
      } />
      <Route path="/home-care/:state/:city" element={
        <FeatureGate featureId="seo.local-care-pages"><LocalCarePage /></FeatureGate>
      } />
      <Route path="/for-discharge-planners" element={<DischargePlannersPage />} />
      <Route path="/for-families" element={<ForFamiliesPage />} />
      {/* Fix-Now Part 3 (Feb 2026 iter) — indexable per-state advance-directive
          references for families. Renders 51-jurisdiction data from
          /api/acp/reference/advance-directives. Unknown codes redirect
          back to /for-families to avoid thin content in the index. */}
      <Route path="/for-families/advance-directives/:state" element={<ForFamiliesAdvanceDirectiveStatePage />} />
      <Route path="/developers" element={<DischargeApiDocs />} />
      <Route path="/for-clinicians" element={
        <FeatureGate featureId="workforce.direct-recruit-marketing">
          <ForCliniciansPage />
        </FeatureGate>
      } />
      {/* Discipline-specific recruitment landing pages — one templated
          page rendered from /data/disciplines.js. Adding a new discipline
          is one entry in that file. */}
      {DISCIPLINES.map((d) => (
        <Route
          key={d.slug}
          path={`/for-${d.slug}`}
          element={
            <FeatureGate featureId="workforce.discipline-landings">
              <DisciplineLandingPage slug={d.slug} />
            </FeatureGate>
          }
        />
      ))}
      {/* No-login senior-friendly Visit Pass (family-books-on-behalf) */}
      <Route path="/visit/:token" element={<VisitPass />} />
      {/* Care-circle invite acceptance (linked from invitation email) */}
      <Route path="/care-circle/join" element={<JoinCareCircle />} />
      {/* Phase 4: white-labeled public micro-agency page (no login) */}
      <Route path="/c/:slug" element={
        <FeatureGate featureId="workforce.micro-agency-mode"><MicroAgencyPage /></FeatureGate>
      } />
      <Route
        path="/planner/dashboard"
        element={
          <ProtectedRoute allowedRoles={['discharge_planner']}>
            <DashboardFocusShell role="planner">
              <PlannerDashboard />
            </DashboardFocusShell>
          </ProtectedRoute>
        }
      />
      {/* ── Phase 1 Spine: discharge-to-acceptance orchestration ── */}
      <Route
        path="/spine/discharge-planner"
        element={
          <ProtectedRoute allowedRoles={['discharge_planner', 'hospital_admin', 'admin']}>
            <SpineDischargePlanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spine/clinician"
        element={
          <ProtectedRoute allowedRoles={['clinician', 'admin']}>
            <SpineClinicianOffers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spine/admin/escalations"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <SpineAdminEscalations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spine/admin/organizations"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <SpineAdminOrganizations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spine/clinician/watches"
        element={
          <ProtectedRoute allowedRoles={['clinician', 'admin']}>
            <SpineClinicianWatches />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spine/admin/watches"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <SpineAdminWatches />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spine/admin/scoreboard/:orgId"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <SpineHospitalScoreboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spine/admin/scoreboard"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <SpineHospitalScoreboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spine/scoreboard"
        element={
          <ProtectedRoute allowedRoles={['hospital_admin', 'discharge_planner', 'provider', 'agency', 'admin']}>
            <SpineHospitalScoreboard />
          </ProtectedRoute>
        }
      />
      {/* ── Provider OS (additive · gated by `provider_os` entitlement, default OFF) ── */}
      <Route
        path="/provider-os"
        element={
          <ProtectedRoute allowedRoles={['admin', 'agency', 'provider', 'clinician', 'hospital_admin']}>
            <ProviderHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/provider-os/work"
        element={
          <ProtectedRoute allowedRoles={['admin', 'agency', 'provider', 'clinician', 'hospital_admin']}>
            <ProviderOSWork />
          </ProtectedRoute>
        }
      />
      <Route
        path="/provider-os/work/:workItemId"
        element={
          <ProtectedRoute allowedRoles={['admin', 'agency', 'provider', 'clinician', 'hospital_admin']}>
            <ProviderOSWorkDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/provider-os/documents"
        element={
          <ProtectedRoute allowedRoles={['admin', 'agency', 'provider', 'clinician', 'hospital_admin']}>
            <ProviderOSDocuments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/provider-os/payouts"
        element={
          <ProtectedRoute allowedRoles={['admin', 'agency', 'provider', 'clinician', 'hospital_admin']}>
            <ProviderOSPayouts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/provider-os/onboard"
        element={
          <ProtectedRoute allowedRoles={['admin', 'agency', 'provider', 'clinician', 'hospital_admin']}>
            <ProviderOSOnboard />
          </ProtectedRoute>
        }
      />
      {/* ── Family-as-View (additive · gated by `family_view`, default OFF) ── */}
      <Route
        path="/family"
        element={
          <ProtectedRoute allowedRoles={['admin', 'patient', 'caregiver', 'family']}>
            <FamilyStatusView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/family/share"
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <FamilyShareManage />
          </ProtectedRoute>
        }
      />
      {/* ── Family Marketplace (PART B · additive · gated by `family_marketplace`, default OFF) ── */}
      <Route
        path="/family/marketplace"
        element={
          <ProtectedRoute allowedRoles={['admin', 'patient', 'caregiver', 'family']}>
            <FamilyMarketplace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payer/dashboard"
        element={
          <ProtectedRoute allowedRoles={['payer', 'admin']}>
            {/* Parked (Phase 7 role alignment) — payer workspace hidden, code preserved.
                Resurrect by swapping ParkedWorkspace back to the
                DashboardFocusShell-wrapped PayerDashboard component. */}
            <ParkedWorkspace />
          </ProtectedRoute>
        }
      />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/support" element={<SupportCenter />} />
      <Route path="/admin/support" element={<AdminSupportInbox />} />
      <Route path="/admin/ops" element={<AdminOpsDashboard />} />
      <Route path="/admin/tenant-health" element={<AdminTenantHealthDashboard />} />
      <Route path="/admin-claim" element={<AdminClaimPage />} />
      <Route 
        path="/auth" 
        element={
          <AuthGuard>
            <AuthPage />
          </AuthGuard>
        } 
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Patient Routes */}
      <Route 
        path="/patient/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <PatientToday />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/dashboard/full" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <PatientDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/assessment" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <PatientAssessment />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/ai-assistant" 
        element={<Navigate to="/patient/navigator" replace />} 
      />
      <Route 
        path="/patient/navigator" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <AICareNavigatorPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/checkin" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <DailyCheckIn />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/book/:clinicianId" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <SmartScheduling />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/family" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <FamilyCaregiverPortal />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/documents" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <DocumentSharing />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/find-clinicians" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <FeatureGate featureId="care.find-clinicians-page">
              <FindClinicians />
            </FeatureGate>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/find-care"
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <FeatureGate featureId="care.find-clinicians-page">
              <PatientFindCare />
            </FeatureGate>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/messages" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <MessagesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/offers" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <OffersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/saved-searches" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <SavedSearches />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/saved-clinicians" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <SavedClinicians />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/benefits" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <BenefitsNavigator />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/care-requests" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <CareRequests />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient/billing" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <PatientBilling />
          </ProtectedRoute>
        } 
      />
      
      {/* Clinician Routes */}
      <Route 
        path="/clinician/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <DashboardFocusShell role="clinician">
              <ClinicianDashboard />
            </DashboardFocusShell>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/assessment" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <ClinicianAssessment />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/credentialing" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <ClinicianCredentialing />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/readmission-risk" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <ClinicianReadmissionRisk />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/voice-notes" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <VoiceNotes />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/documents" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <DocumentSharing />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/risk-assessment/:patientId" 
        element={
          <ProtectedRoute allowedRoles={['clinician', 'admin']}>
            <RiskAssessment />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/notes" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <ClinicalNotes />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/messages" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <MessagesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/offers" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <OffersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/coach" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <AIPracticeCoachPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/care-requests" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <ClinicianCareRequests />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/inbox" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <ClinicianInbox />
          </ProtectedRoute>
        } 
      />
      <Route path="/clinician/schedule" element={<ProtectedRoute allowedRoles={['clinician']}><ClinicianSchedulePage /></ProtectedRoute>} />
      <Route path="/clinician/insurance" element={<ProtectedRoute allowedRoles={['clinician']}><ClinicianInsurancePage /></ProtectedRoute>} />
      <Route path="/clinician/practice-profile" element={<ProtectedRoute allowedRoles={['clinician']}><ClinicianPracticeProfilePage /></ProtectedRoute>} />
      <Route path="/clinician/public-profile" element={<ProtectedRoute allowedRoles={['clinician']}><ClinicianPublicProfilePage /></ProtectedRoute>} />
      <Route 
        path="/clinician/billing" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <RcmDashboard />
          </ProtectedRoute>
        } 
      />
      {/* CaraLoom 2.0 — CEO Decisions #7 / #12 / #21 */}
      <Route 
        path="/operations/executive" 
        element={
          <ProtectedRoute allowedRoles={['clinician', 'agency', 'admin']}>
            <OperationsExecutive />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/referrals/ingest-fhir" 
        element={
          <ProtectedRoute allowedRoles={['discharge_planner', 'agency', 'admin']}>
            <ReferralsFhirIngest />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/denial-prediction" 
        element={
          <ProtectedRoute allowedRoles={['clinician', 'admin']}>
            <DenialPrediction />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/payouts" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <ClinicianPayouts />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/banking" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <ClinicianBanking />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/risk-monitor" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <ClinicianRiskMonitor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/migrate" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <PracticeMigration />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clinician/practice" 
        element={
          <ProtectedRoute allowedRoles={['clinician']}>
            <ClinicianPractice />
          </ProtectedRoute>
        } 
      />
      
      {/* Agency / Organization Routes */}
      <Route 
        path="/agency/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <DashboardFocusShell role="agency">
              <AgencyDashboard />
            </DashboardFocusShell>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/agency/find-clinicians" 
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <AgencyFindClinicians />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/agency/messages" 
        element={
          <ProtectedRoute allowedRoles={['agency']}>
            <MessagesPage />
          </ProtectedRoute>
        } 
      />

      {/* Admin Routes */}
      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DashboardFocusShell role="admin">
              <AdminDashboard />
            </DashboardFocusShell>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/analytics" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminAnalytics />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/safety-shield"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminSafetyShield />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPaymentsOps />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/observability"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminObservability />
          </ProtectedRoute>
        }
      />
      
      {/* Telehealth */}
      <Route 
        path="/privacy/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['patient', 'clinician', 'admin']}>
            <PrivacyDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/telehealth/:appointmentId" 
        element={
          <ProtectedRoute allowedRoles={['patient', 'clinician']}>
            <TelehealthRoom />
          </ProtectedRoute>
        } 
      />

      {/* Stripe Checkout return */}
      <Route 
        path="/payments/success" 
        element={
          <ProtectedRoute allowedRoles={['patient']}>
            <CheckoutSuccess />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback — unknown URLs route home. Unknown /for-<slug> URLs
          render the friendly discipline-not-found branch instead. */}
      <Route path="*" element={<NotFoundRedirect />} />
    </Routes>
  );
}

/**
 * Smart catch-all: any URL that starts with `/for-` but didn't match
 * one of the explicit discipline routes above renders the friendly
 * "that discipline page isn't live yet" branch inside
 * DisciplineLandingPage (which lists the available slugs as chips).
 * Everything else falls back to the homepage.
 */
function NotFoundRedirect() {
  if (typeof window !== 'undefined' && window.location?.pathname?.startsWith('/for-')) {
    return <DisciplineLandingPage slug="__not_found__" />;
  }
  return <Navigate to="/" replace />;
}

function GlobalCopilots() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <>
      <SessionTimeout />
      <ConsentGate />
      <CommandPalette />
      {/*
        CaraDrawer replaces LynkCopilot as the docked AI surface — same
        floating-pill UX, but routes through /api/cara/chat (Vertex BAA)
        with role-scoped live data + the new sample/live mode badge.
        `LynkCopilot` import is preserved (and `routes/lynk.py` backend
        remains live) so any legacy entry-points keep working; only the
        global mount is swapped. Rule Zero respected.
      */}
      <CaraDrawer />
      {(user.role === 'patient' || user.role === 'clinician') && <ProactiveLynk />}
      {user.role === 'patient' && (
        <>
          <EmergencyButton />
          <OnboardingConcierge />
        </>
      )}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      {/* Single source of truth for page titles: the per-page <Seo> component
          (src/components/seo/Seo.jsx) sets <title>/og/twitter/canonical via
          react-helmet-async. We intentionally do NOT set a defaultTitle here
          because authenticated dashboards don't need a title (they're behind
          robots.txt + the auth gate), and public pages always render <Seo>.
          A defaultTitle here would also produce a duplicate <title> tag in
          the prerendered HTML alongside the per-page Helmet — fixed Feb 2026. */}
      <ErrorBoundary>
        <ThemeProvider>
          <AccessibilityModeProvider>
            <AuthProvider>
              <BrowserRouter>
                <AppRoutes />
                <GlobalCopilots />
                <PublicCtaBar />
                <PwaInstallPrompt />
                <Toaster position="top-right" richColors />
              </BrowserRouter>
            </AuthProvider>
          </AccessibilityModeProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
